export interface PasswordPolicySettings {
  minLength: number
  maxLength: number
  requireUppercase: boolean
  requireLowercase: boolean
  requireNumber: boolean
  requireSpecialChar: boolean
}

export const DEFAULT_PASSWORD_POLICY: PasswordPolicySettings = {
  minLength: 8,
  maxLength: 20,
  requireUppercase: false,
  requireLowercase: false,
  requireNumber: true,
  requireSpecialChar: false,
}

const SPECIAL_CHAR_REGEX = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/

export function normalizePasswordPolicy(raw?: Partial<PasswordPolicySettings> | null): PasswordPolicySettings {
  const minLength = clampInt(raw?.minLength, DEFAULT_PASSWORD_POLICY.minLength, 4, 128)
  const maxLength = clampInt(raw?.maxLength, DEFAULT_PASSWORD_POLICY.maxLength, minLength, 256)

  return {
    minLength,
    maxLength,
    requireUppercase: raw?.requireUppercase ?? DEFAULT_PASSWORD_POLICY.requireUppercase,
    requireLowercase: raw?.requireLowercase ?? DEFAULT_PASSWORD_POLICY.requireLowercase,
    requireNumber: raw?.requireNumber ?? DEFAULT_PASSWORD_POLICY.requireNumber,
    requireSpecialChar: raw?.requireSpecialChar ?? DEFAULT_PASSWORD_POLICY.requireSpecialChar,
  }
}

function clampInt(value: unknown, fallback: number, min: number, max: number): number {
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return fallback
  return Math.min(max, Math.max(min, Math.round(n)))
}

export function validatePasswordAgainstPolicy(
  password: string,
  policy: PasswordPolicySettings = DEFAULT_PASSWORD_POLICY,
): string {
  const normalized = normalizePasswordPolicy(policy)

  if (!password) return 'Password is required'

  if (password.length < normalized.minLength) {
    return `Password must be at least ${normalized.minLength} characters long`
  }

  if (password.length > normalized.maxLength) {
    return `Password must be no more than ${normalized.maxLength} characters long`
  }

  if (normalized.requireUppercase && !/[A-Z]/.test(password)) {
    return 'Password must include at least one uppercase letter'
  }

  if (normalized.requireLowercase && !/[a-z]/.test(password)) {
    return 'Password must include at least one lowercase letter'
  }

  if (normalized.requireNumber && !/\d/.test(password)) {
    return 'Password must include at least one number'
  }

  if (normalized.requireSpecialChar && !SPECIAL_CHAR_REGEX.test(password)) {
    return 'Password must include at least one special character'
  }

  const hasAnyRule =
    normalized.requireUppercase ||
    normalized.requireLowercase ||
    normalized.requireNumber ||
    normalized.requireSpecialChar

  if (!hasAnyRule) {
    const hasLetters = /[a-zA-Z]/.test(password)
    const hasNumbers = /\d/.test(password)
    if (!hasLetters || !hasNumbers) {
      return 'Password must include a mix of letters and numbers'
    }
  }

  return ''
}

export function getPasswordRuleChecks(password: string, policy: PasswordPolicySettings) {
  const normalized = normalizePasswordPolicy(policy)
  return {
    minLength: password.length >= normalized.minLength,
    maxLength: password.length <= normalized.maxLength,
    uppercase: !normalized.requireUppercase || /[A-Z]/.test(password),
    lowercase: !normalized.requireLowercase || /[a-z]/.test(password),
    number: !normalized.requireNumber || /\d/.test(password),
    specialChar: !normalized.requireSpecialChar || SPECIAL_CHAR_REGEX.test(password),
  }
}

export function getPasswordPolicyCheckPassed(
  label: string,
  password: string,
  policy: PasswordPolicySettings,
): boolean {
  const checks = getPasswordRuleChecks(password, policy)

  if (label.startsWith('At least')) return checks.minLength
  if (label.startsWith('No more')) return checks.maxLength
  if (label.includes('uppercase')) return checks.uppercase
  if (label.includes('lowercase')) return checks.lowercase
  if (label.includes('number')) return checks.number
  if (label.includes('special')) return checks.specialChar
  if (label.includes('Mix of')) {
    return /[a-zA-Z]/.test(password) && /\d/.test(password)
  }
  return false
}

export function getPasswordPolicyLabels(policy: PasswordPolicySettings): string[] {
  const normalized = normalizePasswordPolicy(policy)
  const labels = [`At least ${normalized.minLength} characters`]
  if (normalized.maxLength < 256) labels.push(`No more than ${normalized.maxLength} characters`)
  if (normalized.requireUppercase) labels.push('One uppercase letter')
  if (normalized.requireLowercase) labels.push('One lowercase letter')
  if (normalized.requireNumber) labels.push('One number')
  if (normalized.requireSpecialChar) labels.push('One special character')
  if (
    !normalized.requireUppercase &&
    !normalized.requireLowercase &&
    !normalized.requireNumber &&
    !normalized.requireSpecialChar
  ) {
    labels.push('Mix of letters and numbers')
  }
  return labels
}
